import { Dialog, Transition } from '@headlessui/react';
import { ButtonCTASmall, LoadingSpinner } from 'components/ui';
import querystring from 'querystring';
import { Fragment } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import { useGetStoreInfoQuery } from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import { ProviderActionCard } from './ProviderActionCard';

export const SegmentIntegrations = () => {
  const history = useHistory();
  const { rawQueryParams } = useParseURLQueryParams();

  const {
    data: { segments },
  } = useAppSelector(composableFiltersState);

  const currentSegment = segments?.find(
    (segment) =>
      segment.segment_name.toLowerCase() ===
      rawQueryParams?.segment_name?.toLowerCase()
  );

  function closeModal() {
    history.goBack();
  }
  const { query } = useParseURLQueryParams();

  const queryParams = querystring.stringify({
    store_name: query.store_name,
  });
  const { data: storeInfo, isFetching } = useGetStoreInfoQuery(queryParams, {
    skip: !!!query.store_name,
  });
  const locale = storeInfo?.primary_locale ?? 'en';

  const providers = storeInfo?.integrations;

  return (
    <Transition appear show as={Fragment}>
      <Dialog
        as="div"
        id="modal"
        className="fixed inset-0 z-10 overflow-y-auto"
        onClose={closeModal}
      >
        <div className="min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <main className="inline-block w-full my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl lg:w-segments-736px rounded-2xl">
              <header className="w-full px-5 py-9 bg-segments-accent-color-1 bg-opacity-5 lg:px-10">
                <div className="flex justify-between">
                  <div>
                    <p className="font-bold text-black text-opacity-50">
                      Integrations
                    </p>
                  </div>
                  <div>
                    <button className="focus:outline-none" onClick={closeModal}>
                      <svg
                        className="w-6 h-6 fill-current text-segments-dark"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="mt-7">
                  <h3 className="text-2xl">
                    {String(currentSegment?.segment_name ?? 'Loading...')}
                  </h3>
                  <p className="mt-2 text-black text-opacity-50">
                    {String(currentSegment?.description ?? 'Loading...')}
                  </p>
                </div>
              </header>

              <div className="px-5 lg:px-10">
                <div className="mt-2 text-base border-b divide-y">
                  <TableLine
                    field="Users"
                    description={String(
                      currentSegment?.segment_size?.toLocaleString(locale) ??
                        'Loading...'
                    )}
                  />
                  <TableLine
                    field="Avg. order value"
                    description={String(
                      currentSegment?.aov?.toLocaleString(locale, {
                        style: 'currency',
                        currency: storeInfo?.currency || 'USD',
                      }) ?? 'Loading...'
                    )}
                  />
                  <TableLine
                    field="Frequency"
                    description={String(currentSegment?.ano ?? 'Loading...')}
                  />
                  <TableLine
                    field="Avg. CLV"
                    description={String(
                      currentSegment?.aclv?.toLocaleString(locale, {
                        style: 'currency',
                        currency: storeInfo?.currency || 'USD',
                      }) ?? 'Loading...'
                    )}
                  />
                </div>

                <div className="mt-6">
                  <div className="space-y-5">
                    {providers?.length ? (
                      providers.map((provider) => (
                        <ProviderActionCard
                          data={provider}
                          key={provider.provider}
                        />
                      ))
                    ) : isFetching ? (
                      <div className="flex items-center justify-center h-60">
                        <LoadingSpinner className="w-10 h-10 text-segments-accent-color-orange" />
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full mt-10">
                        <p>No service provider connected</p>
                        <Link
                          className="px-4 py-2 mt-6 font-bold text-white rounded-md bg-segments-accent-color-1"
                          to={{
                            pathname: '/integrations',
                            search: history.location.search,
                          }}
                        >
                          Connect
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end mx-3 mt-6 mb-8">
                  <div className="">
                    <ButtonCTASmall
                      variant="secondary"
                      type="button"
                      onClick={closeModal}
                    >
                      <p className="px-4 font-normal text-segments-13px">
                        Close
                      </p>
                    </ButtonCTASmall>
                  </div>
                </div>
              </div>
            </main>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

function TableLine({
  field,
  description,
}: {
  field: string;
  description: string;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <p>{field}</p>
      <p>{description}</p>
    </div>
  );
}
